import React from 'react';
import styled from '@emotion/styled';
import { Plus, Trash2 } from 'lucide-react';

const PastMedicalHistoryContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`;

const SectionTitle = styled.h2`
  width: 100%;
  font-family: 'Sofia Sans', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 14px;
  line-height: 17px;
  display: flex;
  align-items: center;
  color: #6D6D71;
`;

const ContentBox = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 16px;
  gap: 20px;
  width: 100%;
  background: #FFFFFF;
  border: 1px solid #C5C5C6;
  border-radius: 12px;
`;

const AddButton = styled.button`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 16px 20px;
  gap: 12px;
  width: 181px;
  height: 54px;
  background: #FFFFFF;
  border: 1px solid #A7A7AA;
  border-radius: 16px;
  font-family: 'Sofia Sans', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 18px;
  line-height: 22px;
  text-align: center;
  color: #2C2C2D;
  cursor: pointer;
`;

const HistoryItem = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: 20px;
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FormLabel = styled.label`
  font-family: 'Sofia Sans', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 19px;
  color: #2C2C2D;
`;

const FormSelect = styled.select`
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 18px 16px;
  gap: 10px;
  width: 240px;
  height: 56px;
  background: #FFFFFF;
  border: 1px solid #A7A7AA;
  border-radius: 12px;
  font-family: 'Sofia Sans', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 19px;
  color: #2C2C2D;
`;

const FormInput = styled.input`
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 18px 16px;
  gap: 10px;
  width: 240px;
  height: 56px;
  background: #FFFFFF;
  border: 1px solid #A7A7AA;
  border-radius: 12px;
  font-family: 'Sofia Sans', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 19px;
  color: #2C2C2D;
`;

const FormTextArea = styled.textarea`
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding: 18px 16px;
  gap: 10px;
  width: 500px;
  height: 73px;
  background: #FFFFFF;
  border: 1px solid #A7A7AA;
  border-radius: 12px;
  font-family: 'Sofia Sans', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 19px;
  color: #2C2C2D;
  resize: vertical;
`;

const DeleteButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid #EF8585;
  border-radius: 4px;
  background: none;
  cursor: pointer;
`;

const medicalConditions = [
  'Hypertension',
  'Diabetes Mellitus (DM)',
  'Ischemic Heart Disease (IHD)',
  'Atrial Fibrillation',
  'Other Arrhythmias',
  'Pacemaker',
  'Prosthetic Heart Valve - Metallic',
  'Prosthetic Heart Valve - Tissue',
  'TAVI',
  'CABG',
  'Cerebrovascular Accident (CVA)',
  'Congenital Heart Disease',
  'Mitral Valve Prolapse',
  'Bicuspid Aortic Valve',
  'Hypertrophic Cardiomyopathy',
  'Other Cardiomyopathy',
  'Endocarditis',
  'Chest Trauma',
  'Rheumatoid Arthritis',
  "Marfan's Syndrome",
  'Other Relevant Conditions'
];

const PastMedicalHistory = ({ pastMedicalHistory, setPastMedicalHistory }) => {
  const addMedicalHistory = () => {
    setPastMedicalHistory([...pastMedicalHistory, { condition: '', yearOfDiagnosis: '', details: '' }]);
  };

  const updateMedicalHistory = (index, field, value) => {
    const updatedHistory = [...pastMedicalHistory];
    updatedHistory[index] = { ...updatedHistory[index], [field]: value };
    setPastMedicalHistory(updatedHistory);
  };

  const deleteMedicalHistory = (index) => {
    const updatedHistory = pastMedicalHistory.filter((_, i) => i !== index);
    setPastMedicalHistory(updatedHistory);
  };

  return (
    <PastMedicalHistoryContainer>
      <SectionTitle>PAST MEDICAL HISTORY</SectionTitle>
      <ContentBox>
        {pastMedicalHistory.map((history, index) => (
          <HistoryItem key={index}>
            <FormField>
              <FormLabel>Condition</FormLabel>
              <FormSelect
                value={history.condition}
                onChange={(e) => updateMedicalHistory(index, 'condition', e.target.value)}
              >
                <option value="">Select condition</option>
                {medicalConditions.map((condition) => (
                  <option key={condition} value={condition}>
                    {condition}
                  </option>
                ))}
              </FormSelect>
            </FormField>
            <FormField>
              <FormLabel>Year of Diagnosis</FormLabel>
              <FormInput
                type="number"
                value={history.yearOfDiagnosis}
                onChange={(e) => updateMedicalHistory(index, 'yearOfDiagnosis', e.target.value)}
                placeholder="Enter year"
              />
            </FormField>
            <FormField>
              <FormLabel>Details</FormLabel>
              <FormTextArea
                value={history.details}
                onChange={(e) => updateMedicalHistory(index, 'details', e.target.value)}
                placeholder="Enter details"
              />
            </FormField>
            <DeleteButton onClick={() => deleteMedicalHistory(index)}>
              <Trash2 size={16} color="#EF8585" />
            </DeleteButton>
          </HistoryItem>
        ))}
        <AddButton onClick={addMedicalHistory}>
          <Plus size={20} />
          Add History
        </AddButton>
      </ContentBox>
    </PastMedicalHistoryContainer>
  );
};

export default PastMedicalHistory;